/**
 * Twilio SMS Service
 * Handles sending SMS messages for payment requests
 */

import { TWILIO_CONFIG, validateTwilioConfig } from '../config/twilioConfig';

export interface TwilioConfig {
  accountSid: string;
  authToken: string;
  phoneNumber: string; // Your Twilio phone number
}

export interface SMSMessage {
  to: string; // Customer phone number
  body: string; // Message content
  from?: string; // Your Twilio phone number
}

class TwilioService {
  private config: TwilioConfig | null = null;
  private isInitialized = false;

  constructor() {
    // Auto-initialize from config if available
    if (validateTwilioConfig()) {
      this.config = TWILIO_CONFIG;
      this.isInitialized = true;
      console.log('✅ Twilio SMS Service auto-initialized with configured credentials');
    }
  }

  /**
   * Initialize Twilio with credentials
   */
  async initialize(config: TwilioConfig): Promise<void> {
    this.config = config;
    this.isInitialized = true;
    console.log('📱 Twilio SMS Service initialized');
  }

  /**
   * Send SMS message
   */
  async sendSMS(message: SMSMessage): Promise<{ success: boolean; messageId?: string; error?: string }> {
    if (!this.isInitialized || !this.config) {
      return {
        success: false,
        error: 'Twilio not initialized. Please provide credentials.',
      };
    }

    try {
      console.log('📱 Sending SMS to:', message.to);
      console.log('📱 Message:', message.body);

      // In development, simulate SMS sending
      if (__DEV__) {
        console.log('🔧 [DEV MODE] Simulating SMS send...');
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
        
        return {
          success: true,
          messageId: `sms_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        };
      }

      // Production: Send real SMS via Twilio API
      const response = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${this.config.accountSid}/Messages.json`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${this.config.accountSid}:${this.config.authToken}`)}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          To: message.to,
          From: message.from || this.config.phoneNumber,
          Body: message.body,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log('✅ SMS sent successfully:', data.sid);
        return {
          success: true,
          messageId: data.sid,
        };
      } else {
        console.error('❌ SMS send failed:', data);
        return {
          success: false,
          error: data.message || 'Failed to send SMS',
        };
      }
    } catch (error: any) {
      console.error('❌ SMS send error:', error);
      return {
        success: false,
        error: error.message || 'Network error sending SMS',
      };
    }
  }

  /**
   * Send payment request SMS
   */
  async sendPaymentRequestSMS(
    customerPhone: string,
    amount: number,
    serviceName: string,
    businessName: string,
    paymentOptions: {
      venmo?: string;
      paypal?: string;
      creditCard?: boolean;
    }
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    let messageBody = `Hi! This is ${businessName}.\n\n`;
    messageBody += `Please pay $${amount} for ${serviceName}.\n\n`;
    messageBody += `Payment options:\n`;

    if (paymentOptions.venmo) {
      messageBody += `• Venmo: ${paymentOptions.venmo}\n`;
    }
    if (paymentOptions.paypal) {
      messageBody += `• PayPal: ${paymentOptions.paypal}\n`;
    }
    if (paymentOptions.creditCard) {
      messageBody += `• Credit Card: Call us to pay\n`;
    }

    messageBody += `\nThank you!`;

    return this.sendSMS({
      to: customerPhone,
      body: messageBody,
    });
  }

  /**
   * Send Venmo payment link SMS
   */
  async sendVenmoLinkSMS(
    customerPhone: string,
    amount: number,
    serviceName: string,
    businessName: string,
    venmoUsername: string
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    const venmoLink = `https://venmo.com/pay-request?amount=${amount}&note=${encodeURIComponent(serviceName)}&recipient=${venmoUsername}`;
    
    const messageBody = `Hi! This is ${businessName}.\n\n`;
    messageBody += `Please pay $${amount} for ${serviceName} via Venmo:\n\n`;
    messageBody += `${venmoLink}\n\n`;
    messageBody += `Or search for @${venmoUsername} in Venmo.\n\n`;
    messageBody += `Thank you!`;

    return this.sendSMS({
      to: customerPhone,
      body: messageBody,
    });
  }

  /**
   * Send PayPal payment link SMS
   */
  async sendPayPalLinkSMS(
    customerPhone: string,
    amount: number,
    serviceName: string,
    businessName: string,
    paypalUsername: string
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    const paypalLink = `https://paypal.me/${paypalUsername}/${amount}?note=${encodeURIComponent(serviceName)}`;
    
    const messageBody = `Hi! This is ${businessName}.\n\n`;
    messageBody += `Please pay $${amount} for ${serviceName} via PayPal:\n\n`;
    messageBody += `${paypalLink}\n\n`;
    messageBody += `Or search for @${paypalUsername} in PayPal.\n\n`;
    messageBody += `Thank you!`;

    return this.sendSMS({
      to: customerPhone,
      body: messageBody,
    });
  }

  /**
   * Check if Twilio is configured
   */
  isConfigured(): boolean {
    return this.isInitialized && this.config !== null;
  }

  /**
   * Get Twilio configuration status
   */
  getStatus(): {
    configured: boolean;
    accountSid: string | null;
    phoneNumber: string | null;
    mode: 'live' | 'dev';
  } {
    return {
      configured: this.isConfigured(),
      accountSid: this.config?.accountSid || null,
      phoneNumber: this.config?.phoneNumber || null,
      mode: this.isConfigured() ? 'live' : 'dev',
    };
  }

  /**
   * Send SMS with simple string parameters
   */
  async sendTextMessage(to: string, message: string): Promise<{ success: boolean; messageId?: string; error?: string }> {
    return this.sendSMS({ to, body: message });
  }
}

// Export singleton instance
export const twilioService = new TwilioService();
export default twilioService;